import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useCollaborationEmail } from '@/contexts/CollaborationEmailContext';
import { useCollaborationEmails } from '@/hooks/useCollaborationEmails';
import { toast } from 'sonner';
import { Code } from 'lucide-react';
import { ShortcodePopup } from '@/components/visual-generation/ShortcodePopup';

export const GlobalEmailDialog = () => {
  const { isOpen, editingEmail, campaignId, closeEmailDialog } = useCollaborationEmail();
  const { createEmail, updateEmail } = useCollaborationEmails(campaignId);
  const subjectRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const [showShortcodePopup, setShowShortcodePopup] = useState(false);
  const [insertTarget, setInsertTarget] = useState<'subject' | 'content'>('content');
  
  const { register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: { 
      subject: '',
      content: '',
      sequence_order: 1,
      delay_days: 0,
      delay_hours: 0,
      send_conditions: {
        stop_if_replied: true,
        stop_if_opened: false
      }
    }
  });

  // Reset form when dialog opens or editing email changes
  useEffect(() => {
    if (isOpen && editingEmail) {
      reset({
        subject: editingEmail.subject,
        content: editingEmail.content,
        sequence_order: editingEmail.sequence_order,
        delay_days: editingEmail.delay_days,
        delay_hours: editingEmail.delay_hours,
        send_conditions: editingEmail.send_conditions
      });
    } else if (isOpen && !editingEmail) {
      reset({
        subject: '',
        content: '',
        sequence_order: 1,
        delay_days: 0,
        delay_hours: 0,
        send_conditions: {
          stop_if_replied: true,
          stop_if_opened: false
        }
      });
    }
  }, [isOpen, editingEmail, reset]);

  const stopIfReplied = watch('send_conditions.stop_if_replied');
  const stopIfOpened = watch('send_conditions.stop_if_opened');
  const subjectValue = watch('subject') || '';
  const contentValue = watch('content') || '';

  const openShortcodePopup = (target: 'subject' | 'content') => {
    setInsertTarget(target);
    setShowShortcodePopup(true);
  };

  const handleInsertShortcode = (shortcode: string) => {
    if (insertTarget === 'subject') {
      const input = subjectRef.current;
      if (input) {
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const newValue = subjectValue.slice(0, start) + shortcode + subjectValue.slice(end);
        setValue('subject', newValue);
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(start + shortcode.length, start + shortcode.length);
        }, 0);
      } else {
        setValue('subject', subjectValue + shortcode);
      }
    } else {
      const textarea = contentRef.current;
      if (textarea) {
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const newValue = contentValue.slice(0, start) + shortcode + contentValue.slice(end);
        setValue('content', newValue);
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + shortcode.length, start + shortcode.length);
        }, 0);
      } else {
        setValue('content', contentValue + shortcode);
      }
    }
    setShowShortcodePopup(false);
  };

  const onSubmit = async (data: any) => {
    if (!campaignId) {
      toast.error('Campaign ID is missing');
      return;
    }

    try {
      if (editingEmail) {
        await updateEmail({ id: editingEmail.id, ...data });
        toast.success('Email updated successfully');
      } else {
        await createEmail(data);
        toast.success('Email created successfully');
      }
      closeEmailDialog();
    } catch (error) {
      console.error('Error saving email:', error);
      toast.error('Failed to save email');
    }
  };

  if (!campaignId) return null;

  return (
    <Dialog open={isOpen} onOpenChange={closeEmailDialog}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={editingEmail ? "Edit Campaign Email" : "Create Campaign Email"}
            popupContext={`/collaboration-campaigns/${campaignId}/emails`}
            isOpen={isOpen}
          >
            {editingEmail ? 'Edit' : 'Add'} Email
          </DialogTitleWithBug>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="sequence_order">Sequence Order *</Label>
              <Input 
                id="sequence_order" 
                type="number" 
                min="1"
                {...register('sequence_order', { required: true, valueAsNumber: true })} 
              />
            </div>
            <div>
              <Label htmlFor="delay_days">Delay (Days)</Label>
              <Input 
                id="delay_days" 
                type="number" 
                min="0"
                {...register('delay_days', { valueAsNumber: true })} 
              />
            </div>
            <div>
              <Label htmlFor="delay_hours">Delay (Hours)</Label>
              <Input 
                id="delay_hours" 
                type="number" 
                min="0"
                max="23"
                {...register('delay_hours', { valueAsNumber: true })} 
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="subject">Email Subject *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => openShortcodePopup('subject')}
                className="h-8 w-8 p-0 hover:bg-muted"
                title="Available Shortcodes"
              >
                <Code className="h-4 w-4" />
              </Button>
            </div>
            <Input 
              id="subject" 
              value={subjectValue}
              onChange={(e) => setValue('subject', e.target.value)}
              placeholder="Subject line for this email"
              ref={subjectRef}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="content">Email Content *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => openShortcodePopup('content')}
                className="h-8 w-8 p-0 hover:bg-muted"
                title="Available Shortcodes"
              >
                <Code className="h-4 w-4" />
              </Button>
            </div>
            <Textarea 
              id="content" 
              value={contentValue}
              onChange={(e) => setValue('content', e.target.value)}
              placeholder="Email body content..."
              rows={10}
              ref={contentRef}
            />
          </div>

          <div className="space-y-3 border-t pt-4">
            <Label>Send Conditions</Label>
            <div className="flex items-center justify-between">
              <Label htmlFor="stop_if_replied" className="font-normal">
                Stop sequence if recipient replied
              </Label>
              <Switch
                id="stop_if_replied"
                checked={stopIfReplied}
                onCheckedChange={(checked) => setValue('send_conditions.stop_if_replied', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="stop_if_opened" className="font-normal">
                Stop sequence if recipient opened previous email
              </Label>
              <Switch
                id="stop_if_opened"
                checked={stopIfOpened}
                onCheckedChange={(checked) => setValue('send_conditions.stop_if_opened', checked)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeEmailDialog}>Cancel</Button>
            <Button type="submit">{editingEmail ? 'Update' : 'Create'}</Button>
          </div>
        </form>

        <ShortcodePopup
          open={showShortcodePopup}
          onOpenChange={setShowShortcodePopup}
          onInsert={handleInsertShortcode}
        />
      </DialogContent>
    </Dialog>
  );
};
